import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { Pagination } from '../../components/Pagination';

const COLUMNS = [
    { key: 'supplySku', label: 'Supply SKU' },
    { key: 'productName', label: 'Product Name' },
    { key: 'inactiveSkus', label: 'Inactive SKUs (Product)' },
    { key: 'missingInactiveSku', label: 'Missing Inactive SKU' },
];

const InactiveSkus = () => {
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({ total: 0, productsAffected: 0 });
    const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });
    const [currentPage, setCurrentPage] = useState(1);
    const [currentItemsPerPage, setCurrentItemsPerPage] = useState(50);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        const timeoutId = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(timeoutId);
    }, [search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, currentItemsPerPage]);

    const fetchData = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: String(currentPage),
                pageSize: String(currentItemsPerPage),
            });
            if (debouncedSearch) {
                params.append('search', debouncedSearch);
            }

            const response = await axios.get(
                `/api/v1/products/inactive-skus-missing-mappings?${params.toString()}`
            );
            if (requestId !== requestIdRef.current) {
                return;
            }

            setData(response.data?.data || []);
            setSummary(
                response.data?.summary || {
                    total: 0,
                    productsAffected: 0,
                }
            );
            setPagination(
                response.data?.pagination || {
                    page: 1,
                    pageSize: 50,
                    total: 0,
                    totalPages: 1,
                }
            );
        } catch (fetchError) {
            if (requestId !== requestIdRef.current) {
                return;
            }
            console.error('Failed to load inactive SKUs missing mappings:', fetchError);
            setData([]);
            setError(fetchError?.response?.data?.error || 'Failed to load inactive SKUs comparison.');
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [currentPage, currentItemsPerPage, debouncedSearch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const rangeStart = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
    const rangeEnd = Math.min(pagination.page * pagination.pageSize, pagination.total);

    return (
        <main className="w-full h-full px-4 sm:px-6 lg:px-8">
            <div className="space-y-4 my-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Inactive SKUs Comparison</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Products with inactive SKUs listed on the product record but not mapped in inventory SKU
                        mappings (execution SKU linked to the supply product).
                    </p>
                </div>

                <div className="flex flex-wrap gap-3">
                    <div className="rounded-lg bg-white border border-gray-200 px-4 py-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500">Missing Mappings</div>
                        <div className="text-lg font-semibold text-red-700">{summary.total}</div>
                    </div>
                    <div className="rounded-lg bg-white border border-gray-200 px-4 py-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500">Products Affected</div>
                        <div className="text-lg font-semibold text-gray-900">{summary.productsAffected}</div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search by supply SKU, missing inactive SKU, or product name"
                        className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {isLoading ? (
                    <div className="relative h-64">
                        <LoaderSpinner colorSpinner="black" width="70" height="70" border="5" />
                    </div>
                ) : (
                    <>
                        <div className="bg-white rounded-lg overflow-auto border border-gray-200">
                            <table className="min-w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        {COLUMNS.map((column) => (
                                            <th
                                                key={column.key}
                                                className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                            >
                                                {column.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={COLUMNS.length}
                                                className="py-8 px-4 text-center text-sm text-gray-500"
                                            >
                                                {summary.total === 0
                                                    ? 'All inactive SKUs on products are mapped in inventory SKU mappings.'
                                                    : 'No rows match the current search.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        data.map((row, index) => (
                                            <tr
                                                key={`${row.supplyProductId}-${row.missingInactiveSku}-${index}`}
                                                className={`border-b border-gray-100 last:border-b-0 ${
                                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                                                }`}
                                            >
                                                {COLUMNS.map((column) => (
                                                    <td
                                                        key={column.key}
                                                        className={`py-3 px-4 text-sm whitespace-nowrap ${
                                                            column.key === 'missingInactiveSku'
                                                                ? 'font-mono font-medium text-red-800 bg-red-50'
                                                                : column.key === 'supplySku' || column.key === 'inactiveSkus'
                                                                  ? 'font-mono text-gray-900'
                                                                  : 'text-gray-900'
                                                        }`}
                                                    >
                                                        {row[column.key] || ''}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            currentItemsPerPage={currentItemsPerPage}
                            currentPage={currentPage}
                            rangeRows={`${rangeStart}-${rangeEnd} of ${pagination.total}`}
                            isTheLastPage={currentPage >= pagination.totalPages}
                            totalPages={pagination.totalPages}
                            setCurrentPage={setCurrentPage}
                            setCurrentItemsPerPage={setCurrentItemsPerPage}
                        />
                    </>
                )}
            </div>
        </main>
    );
};

export default InactiveSkus;
